import React from "react";
import type { TFunction } from "i18next";
import {
  Activity,
  Bed,
  Briefcase,
  Building,
  CalendarClock,
  ClipboardList,
  ConciergeBell,
  LineChart,
  CircleDollarSign,
  CircleHelp,
  Globe,
  MessageCircle,
  MessageSquare,
  MailCheck,
  Plane,
  Users,
  Settings,
  Package2,
  MegaphoneIcon,
  LayoutDashboard,
  User,
  Files,
  Puzzle,
  Shield,
  File,
  FileDown,
  Trash2,
  Undo2,
  KeyRound,
  Hotel,
  TabletSmartphone,
  ServerCog,
  SquareUserRound,
  WandSparkles,
  Luggage,
  Megaphone,
  MessagesSquare,
  HandPlatter,
  Handshake,
  MapPin,
  CreditCard,
  Gauge,
  Wallet,
} from "lucide-react";
import { matchesNavigationPath } from "@/components/layout/utils/isNavLinkActive";

export interface NavItem {
  title: string;
  href: string;
  icon: React.ReactNode;
  iconColor?: string;
  total?: number;
  newCount?: number;
  items?: NavItem[];
  alwaysVisible?: boolean;
  // When true (only meaningful for items with `items`), clicking the row
  // label toggles the submenu instead of navigating to `href` — use this
  // when `href` isn't a real page of its own (e.g. it points to an
  // unrelated main-layout route reused only as a grouping placeholder).
  isGroupOnly?: boolean;
}

export interface NavGroup {
  title?: string;
  items: NavItem[];
}

type HotelSidebarRole = "HOTEL_ADMIN" | "FRONT_DESK" | "STAFF" | undefined;

const createHotelBillingNavigationItem = (
  t: TFunction,
  role?: HotelSidebarRole,
): NavItem => {
  if (role === "HOTEL_ADMIN") {
    return {
      title: t("layout.nav.items.billingSettings"),
      href: "/billing-settings",
      icon: <Package2 />,
      iconColor: "text-sidebar-foreground",
    };
  }

  return {
    title: t("layout.nav.items.billingTransactions"),
    href: "/billing-transactions",
    icon: <Package2 />,
    iconColor: "text-sidebar-foreground",
  };
};

const createHotelOperationalNavigationGroups = (
  t: TFunction,
  arrivals?: number,
  departures?: number,
  inHouse?: number,
  requestsCount?: number,
  requestsUnread?: number,
  chatsTotal?: number,
  chatsUnread?: number,
  internalChatsTotal?: number,
  internalChatsUnread?: number,
): NavGroup[] => [
  {
    items: [
      {
        title: t("layout.nav.items.dashboard"),
        href: "/dashboard",
        icon: <LayoutDashboard />,
        iconColor: "text-royal-glow-3-light",
      },
    ],
  },
  {
    title: t("layout.nav.groups.guests"),
    items: [
      {
        title: t("layout.nav.items.arrivals"),
        href: "/arrivals",
        icon: <Plane />,
        iconColor: "text-eathy-comfort-2",
        ...(arrivals !== undefined ? { total: arrivals } : {}),
      },
      {
        title: t("layout.nav.items.inHouse"),
        href: "/in-house",
        icon: <Bed />,
        iconColor: "text-eathy-comfort-2",
        ...(inHouse !== undefined ? { total: inHouse } : {}),
      },
      {
        title: t("layout.nav.items.departures"),
        href: "/departures",
        icon: <Briefcase />,
        iconColor: "text-eathy-comfort-2",
        ...(departures !== undefined ? { total: departures } : {}),
      },
    ],
  },
  {
    title: t("layout.nav.groups.inbox"),
    items: [
      {
        title: t("layout.nav.items.chats"),
        href: "/chats",
        icon: <MessageSquare />,
        iconColor: "text-royal-glow-3-light",
        ...(chatsTotal !== undefined ? { total: chatsTotal } : {}),
        ...(chatsUnread !== undefined && chatsUnread > 0
          ? { newCount: chatsUnread }
          : {}),
      },
      {
        title: t("layout.nav.items.requests"),
        href: "/requests",
        icon: <ClipboardList />,
        iconColor: "text-royal-glow-3-light",
        ...(requestsCount !== undefined ? { total: requestsCount } : {}),
        ...(requestsUnread !== undefined && requestsUnread > 0
          ? { newCount: requestsUnread }
          : {}),
      },
      {
        title: t("layout.nav.items.feedbacks"),
        href: "/feedbacks",
        icon: <MegaphoneIcon />,
        iconColor: "text-royal-glow-3-light",
      },
    ],
  },
  {
    title: t("layout.nav.groups.management"),
    items: [
      {
        title: t("layout.nav.items.services"),
        href: "/services",
        icon: <ConciergeBell />,
        iconColor: "text-skyline-2",
      },
      {
        title: t("layout.nav.items.staff"),
        href: "/staff",
        icon: <Users />,
        iconColor: "text-skyline-2",
        items: [
          {
            title: t("layout.nav.items.staffSchedule"),
            href: "/staff-schedule",
            icon: <CalendarClock />,
            iconColor: "text-skyline-2",
          },
        ],
      },
      {
        title: t("layout.nav.items.internalChat"),
        href: "/internal-chats",
        icon: <MessageCircle />,
        iconColor: "text-skyline-2",
        ...(internalChatsTotal !== undefined
          ? { total: internalChatsTotal }
          : {}),
        ...(internalChatsUnread !== undefined && internalChatsUnread > 0
          ? { newCount: internalChatsUnread }
          : {}),
      },
      {
        title: t("layout.nav.items.analytics"),
        href: "/analytics",
        icon: <LineChart />,
        iconColor: "text-skyline-2",
      },
    ],
  },
];

const createHotelSettingsNavigationGroups = (
  t: TFunction,
  role?: HotelSidebarRole,
): NavGroup[] => [
  {
    items: [
      {
        title: t("layout.nav.items.dashboard"),
        href: "/dashboard",
        icon: <Undo2 />,
        iconColor: "text-royal-glow-3-light",
      },
    ],
  },
  {
    title: t("layout.settingsNav.groups.hotel"),
    items: [
      {
        title: t("layout.settingsNav.items.generalInfo"),
        href: "/general-info",
        icon: <Hotel />,
        iconColor: "text-skyline-2",
      },
      {
        title: t("layout.settingsNav.items.rooms"),
        href: "/reservations/rooms",
        icon: <KeyRound />,
        iconColor: "text-skyline-2",
      },
      {
        title: t("layout.settingsNav.items.amenities"),
        href: "/amenities",
        icon: <ConciergeBell />,
        iconColor: "text-skyline-2",
      },
      {
        title: t("layout.settingsNav.items.pages"),
        href: "/custom-pages",
        icon: <TabletSmartphone />,
        iconColor: "text-skyline-2",
      },
    ],
  },
  {
    title: t("layout.settingsNav.groups.guestExperience"),
    items: [
      {
        title: t("layout.settingsNav.items.pms"),
        href: "/pms-integration",
        icon: <ServerCog />,
        iconColor: "text-eathy-comfort-2",
      },
      {
        title: t("layout.settingsNav.items.confirmation"),
        href: "/notifications-settings",
        icon: <MailCheck />,
        iconColor: "text-eathy-comfort-2",
      },
      {
        title: t("layout.nav.items.faqs"),
        href: "/faqs",
        icon: <CircleHelp />,
        iconColor: "text-eathy-comfort-2",
      },
      {
        title: t("layout.nav.items.preCheckIn"),
        href: "/checkin-settings",
        icon: <SquareUserRound />,
        iconColor: "text-eathy-comfort-2",
      },
      {
        title: t("layout.settingsNav.items.messagingSettings"),
        href: "/messaging-settings",
        icon: <MessagesSquare />,
        iconColor: "text-eathy-comfort-2",
      },
      {
        title: t("layout.settingsNav.items.appCustomization"),
        href: "/mobile-settings",
        icon: <WandSparkles />,
        iconColor: "text-eathy-comfort-2",
      },
      {
        title: t("layout.nav.items.preCheckOut"),
        href: "/checkout-settings",
        icon: <Luggage />,
        iconColor: "text-eathy-comfort-2",
      },
      {
        title: t("layout.settingsNav.items.feedback"),
        href: "/feedback-settings",
        icon: <Megaphone />,
        iconColor: "text-eathy-comfort-2",
      },
    ],
  },
  {
    title: t("layout.settingsNav.groups.communication"),
    items: [
      {
        title: t("layout.settingsNav.items.communicationIntegrations"),
        href: "/communication-integrations",
        icon: <ServerCog />,
        iconColor: "text-eathy-comfort-2",
      },
      {
        title: t("layout.settingsNav.items.communicationDeliveries"),
        href: "/communication-deliveries",
        icon: <MailCheck />,
        iconColor: "text-eathy-comfort-2",
      },
    ],
  },
  {
    title: t("layout.settingsNav.groups.modules"),
    items: [
      {
        title: t("layout.nav.items.chats"),
        href: "/communication-channels",
        icon: <MessagesSquare />,
        iconColor: "text-royal-glow-3-light",
        items: [
          {
            title: t("layout.nav.items.chatsAi"),
            href: "/ai",
            icon: <WandSparkles />,
            iconColor: "text-royal-glow-3-light",
          },
          {
            title: t("layout.nav.items.cannedResponses"),
            href: "/canned-responses",
            icon: <MessageSquare />,
            iconColor: "text-royal-glow-3-light",
          },
        ],
      },
      {
        title: t("layout.nav.items.services"),
        href: "/guesteria-partners",
        icon: <HandPlatter />,
        iconColor: "text-skyline-2",
        items: [
          {
            title: t("layout.nav.items.servicesAdvanced"),
            href: "/partner-integration-settings",
            icon: <ServerCog />,
            iconColor: "text-skyline-2",
          },
        ],
      },
      {
        title: t("layout.nav.items.requests"),
        href: "/requests-settings",
        icon: <ClipboardList />,
        iconColor: "text-royal-glow-3-light",
      },
      createHotelBillingNavigationItem(t, role),
    ],
  },
];

export const HOTEL_SETTINGS_MODE_PATHS = [
  "/general-info",
  "/reservations/rooms",
  "/amenities",
  "/custom-pages",
  "/mobile-settings",
  "/faqs",
  "/notifications-settings",
  "/checkin-settings",
  "/messaging-settings",
  "/feedback-settings",
  "/checkout-settings",
  "/services/categories",
  "/billing-settings",
  "/partner-integration-settings",
  "/guesteria-partners",
  "/ai",
  "/canned-responses",
  "/pms-integration",
  "/billing-transactions",
  "/requests-settings",
  "/communication-integrations",
  "/communication-channels",
  "/communication-deliveries",
];

export const HOTEL_DEFAULT_MODE_PATHS = [
  "/dashboard",
  "/arrivals",
  "/in-house",
  "/departures",
  "/reservations",
  "/reservations/guests",
  "/staff",
  "/staff-schedule",
  "/internal-chats",
  "/analytics",
  "/analytics-fd",
  "/chats",
];

export const isHotelSettingsModePath = (pathname: string): boolean =>
  HOTEL_SETTINGS_MODE_PATHS.some((path) =>
    matchesNavigationPath(pathname, path),
  );

export const isHotelDefaultModePath = (pathname: string): boolean =>
  HOTEL_DEFAULT_MODE_PATHS.some((path) =>
    matchesNavigationPath(pathname, path),
  );

export const buildNavigationGroups = (
  t: TFunction,
  arrivals?: number,
  departures?: number,
  inHouse?: number,
  requestsCount?: number,
  requestsUnread?: number,
  chatsTotal?: number,
  chatsUnread?: number,
  internalChatsTotal?: number,
  internalChatsUnread?: number,
): NavGroup[] =>
  createHotelOperationalNavigationGroups(
    t,
    arrivals,
    departures,
    inHouse,
    requestsCount,
    requestsUnread,
    chatsTotal,
    chatsUnread,
    internalChatsTotal,
    internalChatsUnread,
  );

export const buildHotelSettingsNavigationGroups = (
  t: TFunction,
  role?: HotelSidebarRole,
): NavGroup[] => createHotelSettingsNavigationGroups(t, role);

export const buildNavigationGroupsAdmin = (t: TFunction): NavGroup[] => [
  {
    items: [
      {
        title: t("layout.adminNav.items.hotelBrands"),
        href: "/hotels",
        icon: <Building />,
        iconColor: "text-royal-glow-3",
      },
      {
        title: t("layout.adminNav.items.users"),
        href: "/users",
        icon: <Users />,
        iconColor: "text-sidebar-foreground",
      },
      {
        title: t("layout.adminNav.items.billing.label"),
        href: "/billing-transactions",
        icon: <Package2 />,
        iconColor: "text-sidebar-foreground",
        items: [
          {
            title: t("layout.adminNav.items.billingTransactions"),
            href: "/billing-transactions",
            icon: <Package2 />,
            iconColor: "text-sidebar-foreground",
          },
          {
            title: t("layout.adminNav.items.billing.subscriptions"),
            href: "/billing-subscriptions",
            icon: <CreditCard />,
            iconColor: "text-sidebar-foreground",
          },
          {
            title: t("layout.adminNav.items.billing.subscriptionPayments"),
            href: "/subscription-payments",
            icon: <CreditCard />,
            iconColor: "text-sidebar-foreground",
          },
          {
            title: t("layout.adminNav.items.guesteriaPartnersRevenue"),
            href: "/guesteria-partners-revenue",
            icon: <CircleDollarSign />,
            iconColor: "text-sidebar-foreground",
          },
        ],
      },
      {
        title: t("layout.adminNav.items.analytics"),
        href: "/analytics",
        icon: <LineChart />,
        iconColor: "text-sidebar-foreground",
      },
      {
        title: t("layout.adminNav.items.settings.label"),
        href: "/settings",
        icon: <Settings />,
        iconColor: "text-muted-foreground",
        items: [
          {
            title: t("layout.adminNav.items.settings.general"),
            href: "/settings",
            icon: <Settings />,
            iconColor: "text-sidebar-foreground",
          },
          {
            title: t("layout.adminNav.items.settings.notificationsSettings"),
            href: "/notifications-settings",
            icon: <MailCheck />,
            iconColor: "text-sidebar-foreground",
          },
        ],
      },
    ],
  },
];

export const buildNavigationGroupsSuperAdmin = (t: TFunction): NavGroup[] => [
  {
    items: [
      {
        title: t("layout.superAdminNav.items.tenants"),
        href: "/tenants",
        icon: <User />,
        iconColor: "text-royal-glow-3",
      },
      {
        title: t("layout.superAdminNav.items.users"),
        href: "/users",
        icon: <Users />,
        iconColor: "text-sidebar-foreground",
      },
      {
        title: t("layout.superAdminNav.items.subscriptions"),
        href: "/subscriptions",
        icon: <Package2 />,
        iconColor: "text-chart-2",
      },
      {
        title: t("layout.superAdminNav.items.partners"),
        href: "/partners",
        icon: <Handshake />,
        iconColor: "text-sidebar-foreground",
        items: [
          {
            title: t("layout.superAdminNav.items.destinations"),
            href: "/partners/destinations",
            icon: <MapPin />,
            iconColor: "text-sidebar-foreground",
          },
        ],
      },
      {
        // Task 0029 — no dedicated `layout.superAdminNav` key exists for
        // this yet; reusing the page's own i18n key (`partners.admin.summary.title`
        // = "Partner revenue"), already added this session, rather than
        // touching `en/*.json` for a new nav label.
        title: t("partners.admin.summary.title"),
        href: "/partners/revenue",
        icon: <Wallet />,
        iconColor: "text-sidebar-foreground",
      },
      // Hidden for SUPER_ADMIN / GUESTERIA_ADMIN — use Platform Analytics (/admin/analytics).
      // TENANT_ADMIN keeps /analytics via buildNavigationGroupsAdmin.
      // {
      //   title: t("layout.superAdminNav.items.analytics"),
      //   href: "/analytics",
      //   icon: <LineChart />,
      //   iconColor: "text-chart-1",
      // },
      {
        title: t("layout.superAdminNav.items.platformAnalytics"),
        href: "/admin/analytics",
        icon: <Gauge />,
        iconColor: "text-sidebar-foreground",
      },
      {
        title: t("monitoring.nav.dashboard"),
        href: "/monitoring",
        icon: <Activity />,
        iconColor: "text-sidebar-foreground",
        items: [
          {
            title: t("monitoring.nav.settings"),
            href: "/monitoring-settings",
            icon: <Settings />,
            iconColor: "text-sidebar-foreground",
          },
          {
            title: t("monitoring.nav.usersMonitoring"),
            href: "/users-monitoring",
            icon: <Shield />,
            iconColor: "text-sidebar-foreground",
          },
        ],
      },
      {
        title: t("layout.superAdminNav.items.settings.label"),
        href: "/regions",
        icon: <Settings />,
        iconColor: "text-muted-foreground",
        items: [
          {
            title: t("layout.superAdminNav.items.settings.regions"),
            href: "/regions",
            icon: <Globe />,
            iconColor: "text-sidebar-foreground",
          },
          {
            title: t("layout.superAdminNav.items.settings.storage_management"),
            href: "/storage",
            icon: <Files />,
            iconColor: "text-sidebar-foreground",
          },
          {
            title: t("layout.superAdminNav.items.settings.modules"),
            href: "/modules",
            icon: <Puzzle />,
            iconColor: "text-sidebar-foreground",
          },
          {
            title: t("layout.superAdminNav.items.settings.privacy_policy"),
            href: "/edit-privacy-policy",
            icon: <Shield />,
            iconColor: "text-sidebar-foreground",
          },
          {
            title: t("layout.superAdminNav.items.settings.terms_of_use"),
            href: "/edit-terms-of-use",
            icon: <File />,
            iconColor: "text-sidebar-foreground",
          },
          {
            title: t("layout.superAdminNav.items.settings.appLinks"),
            href: "/app-links-settings",
            icon: <CircleHelp />,
            iconColor: "text-sidebar-foreground",
          },
          {
            title: t("dataDeletion.audit.nav"),
            href: "/data-deletion",
            icon: <Trash2 />,
            iconColor: "text-sidebar-foreground",
          },
          {
            title: t("dataExport.audit.nav"),
            href: "/data-export",
            icon: <FileDown />,
            iconColor: "text-sidebar-foreground",
          },
        ],
      },
    ],
  },
];
